import React, { useEffect, useState } from "react";
import { Card, CardBody, Row, CardTitle, Col } from "reactstrap";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import { SvRequestStatusConfigurations } from "./configuration/SvRequestStatusConfigurations";
import { OrderDto } from "services/apis/types/order/OrderDto";
import { OrderTypeConfigurations } from "./configuration/OrderTypeConfigurations";
import { getNumberStatus } from "services/apis/types/order/NumberStatus";
import { NoteDto } from "services/apis/types/order/NoteDto";
import { OrderApi } from "services/apis/OrderApi";
import { handleError } from "services/util/ApiUtil";
import { useIsMounted } from "services/customHooks/useIsMounted";

type Props = {
  order: OrderDto;
};

const showNoteStatuses = ["Rejected", "CSRInvalid"];
const Statuses = [
  "CSRPending",
  "CSRRequested",
  "PreOrderPending",
  "Submitted",
  "Pending",
  "Authorized",
  "ActivatePending"
];

export default function OrderStatusCard(props: Props) {
  const isMounted = useIsMounted();
  const [latestNote, setLatestNote] = useState<NoteDto>();
  const activationDate =
    props.order.autoActivation +
    (props.order.activationTimeZone ? " " + props.order.activationTimeZone : "");

  useEffect(() => {
    OrderApi.getNotes(props.order.orderId)
      .then((notes: NoteDto[]) => {
        if (isMounted && notes?.length > 0) {
          const rejectionNotes = notes.filter(
            (note) => (note.title === "CSR rejected") || (note.title === "LSR rejected")
          );

          if (rejectionNotes.length > 0) {
            // Sort rejection notes by date and get the latest
            const sorted = [...rejectionNotes].sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
            setLatestNote(sorted[0]);
          } else {
            // Optional: handle case when no rejection notes exist
            setLatestNote(undefined);
          }
        }
      })
  .catch((error) => handleError(error));
  }, [props.order, isMounted]);
  return (
    <Card
      className={classNames(
        SvRequestStatusConfigurations[props.order.status]?.backgroundColor ||
          OrderTypeConfigurations[props.order.orderType]?.backgroundColor,
        "flex-grow-1"
      )}>
      <CardBody>
        <Row>
          <div className="col">
            <CardTitle tag="h5" className="text-uppercase text-white text-muted mb-0">
              <FormattedMessage id="orders.orderDetails.status" />
            </CardTitle>
            <span className="h2 font-weight-bold text-white mb-0">
              {getNumberStatus(props.order.status)}
            </span>
          </div>
          {SvRequestStatusConfigurations[props.order.status]?.iconClass && (
            <Col className="col-auto">
              <div className="icon icon-shape bg-white rounded-circle shadow">
                <i className={`${SvRequestStatusConfigurations[props.order.status]?.iconClass}`} />
              </div>
            </Col>
          )}
        </Row>
        <Row>
          <div className="col mlb-2">
            {props.order.autoActivation && Statuses.includes(props.order.status) && (
              <span className="h5 text-white mb-0">
                <FormattedMessage
                  id="orders.orderDetails.activationDate"
                  values={{ activationDate: activationDate }}
                />
              </span>
            )}
          </div>
        </Row>
        {showNoteStatuses.includes(props.order.status)
          ? latestNote?.note && (
              <p className="mt-3 mb-0 text-sm">
                <span className="text-white">{latestNote?.note}</span>
              </p>
            )
          : SvRequestStatusConfigurations[props.order.status]?.messageId && (
              <p className="mt-3 mb-0 text-sm">
                <span className="text-white">
                  {latestNote?.note && (
                    <FormattedMessage
                      id={SvRequestStatusConfigurations[props.order.status]?.messageId}
                    />
                  )}
                </span>
              </p>
            )}
      </CardBody>
    </Card>
  );
}
